import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/lib/constants/routes';
import { formatDate } from '@/lib/utils/date.utils';
import { PARTNER_OFFERS_STRINGS as S } from '../partner-offers.constants';
import type { PartnerOfferData } from '../api/partner-offers.api';

/**
 * The four status-grouped row treatments on the Perks & Offers console.
 *
 * Extracted from `PartnerOffersConsolePage` in MYN-106: adding the Rejected
 * section pushed that file toward the 400-LOC yellow band, and the rule is
 * that the change which crosses it carries the split. One file rather than
 * four: they are four variants of one thing - a row in one console's
 * status-grouped list - and each is a dozen lines.
 *
 * Figma node 322:6281 gives each section a different treatment, which is why
 * these are distinct components rather than one row with a `variant` prop.
 */

/** The offer's availability line, shown on every row. Null expiry is a real state, not missing data. */
function expiryLine(offer: PartnerOfferData): string {
  return offer.expiresAt ? `Expires ${formatDate(offer.expiresAt)}` : S.NO_EXPIRY;
}

export function AwaitingOfferRow({
  offer,
  onWithdraw,
  isWithdrawing,
}: {
  offer: PartnerOfferData;
  onWithdraw: (id: string) => void;
  isWithdrawing: boolean;
}) {
  return (
    <div className="border-divider flex items-start justify-between gap-4 border-b pb-4 last:border-b-0 last:pb-0">
      <div>
        <p className="text-navy font-bold">{offer.title}</p>
        <p className="text-p6 text-slate mt-1 line-clamp-2">{offer.description}</p>
        <p className="text-slate mt-2 text-[11px]">{expiryLine(offer)}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <Badge variant="warning" size="sm">
          Awaiting Review
        </Badge>
        <p className="text-slate text-[11px]">Submitted {formatDate(offer.submittedAt)}</p>
        <button
          type="button"
          onClick={() => onWithdraw(offer.id)}
          disabled={isWithdrawing}
          className="text-p6 text-red cursor-pointer font-semibold hover:opacity-75 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {S.WITHDRAW}
        </button>
      </div>
    </div>
  );
}

export function AcceptedOfferRow({
  offer,
  onPublish,
  isPublishing,
}: {
  offer: PartnerOfferData;
  onPublish: (id: string) => void;
  isPublishing: boolean;
}) {
  return (
    <div className="rounded-card border-green border-[1.5px] p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-navy font-bold">{offer.title}</p>
          <p className="text-p6 text-slate mt-1 line-clamp-2">{offer.description}</p>
          <p className="text-slate mt-2 text-[11px]">{expiryLine(offer)}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Badge variant="success" size="sm">
            Accepted
          </Badge>
          <Button type="button" variant="green" onClick={() => onPublish(offer.id)} disabled={isPublishing}>
            {S.PUBLISH}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function LiveOfferRow({
  offer,
  onDeactivate,
  isDeactivating,
}: {
  offer: PartnerOfferData;
  onDeactivate: (id: string) => void;
  isDeactivating: boolean;
}) {
  return (
    <div className="rounded-card border-divider shadow-card flex items-center justify-between gap-4 border bg-white p-4">
      <div>
        <p className="text-navy font-bold">{offer.title}</p>
        <p className="text-p6 text-slate mt-1 line-clamp-2">{offer.description}</p>
        <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
          <span className="text-teal2">Published {formatDate(offer.publishedAt)}</span>
          <span className="text-slate">{expiryLine(offer)}</span>
          {/* MYN-106 AC7. Shown only on Live rows: a click count is about an
              offer members can actually see. The label says where the number
              comes from, because it reads zero for every offer until the
              member Perks page ships (MYN-107) - without that, zero reads as
              "nobody is interested" rather than "nothing can count yet". */}
          <span className="text-slate">
            {offer.redemptionCount} {S.CTA_CLICKS}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <Badge variant="teal" size="sm">
          Live
        </Badge>
        <Button type="button" variant="outline" onClick={() => onDeactivate(offer.id)} disabled={isDeactivating}>
          {S.DEACTIVATE}
        </Button>
      </div>
    </div>
  );
}

/**
 * A rejected offer and the reason MNR gave (MYN-106 AC6).
 *
 * Until this shipped, `mnrRejectionReason` was fetched and rendered nowhere:
 * a partner was told an offer was refused with no way to see why, and - since
 * `rejected` had no transition out of it at all - no way to act on it either.
 * The Edit action is the way back: the Backend returns a rejected offer to
 * Draft when the partner edits it, and the existing Send for Review flow picks
 * it up from there.
 */
export function RejectedOfferRow({ offer }: { offer: PartnerOfferData }) {
  return (
    <div className="rounded-card border-red/30 bg-red/5 border p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-navy font-bold">{offer.title}</p>
          <p className="text-p6 text-slate mt-1 line-clamp-2">{offer.description}</p>
          {offer.mnrRejectionReason && (
            <p className="text-p6 text-navy mt-2">
              <span className="font-semibold">{S.REJECTION_REASON_LABEL}</span>{' '}
              {offer.mnrRejectionReason}
            </p>
          )}
          <p className="text-slate mt-2 text-[11px]">{S.REJECTED_NEXT_STEP}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Badge variant="danger" size="sm">
            Rejected
          </Badge>
          <Link to={ROUTES.PARTNER_OFFER_EDIT.replace(':offerId', offer.id)}>
            <Button type="button" variant="outline">
              {S.EDIT_AND_RESUBMIT}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
